/**
 * Servicio de Cajas y Efectivo
 * Maneja cajas, categorías, movimientos de efectivo,
 * documentos de respaldo y reportes
 */
import api from './api';

// ============================================================================
// CAJAS
// ============================================================================

/**
 * Obtener todas las cajas con su saldo actual
 * @param {Object} filters - Filtros (activa)
 * @returns {Promise<Array>} Lista de cajas
 */
export const getCajas = async (filters = {}) => {
  try {
    const response = await api.get('/cajas', { params: filters });
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener cajas:', error);
    throw error;
  }
};

/**
 * Obtener una caja por ID
 * @param {string|number} id - ID de la caja
 * @returns {Promise<Object>} Datos de la caja
 */
export const getCajaById = async (id) => {
  try {
    const response = await api.get(`/cajas/${id}`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener caja:', error);
    throw error;
  }
};

/**
 * Obtener la caja del día del usuario autenticado
 * (permite decidir entre "Abrir Caja" y "Editar Caja")
 * @returns {Promise<Object|null>} Caja del día o null
 */
export const getMiCajaHoy = async () => {
  try {
    const response = await api.get('/cajas/mia/hoy');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener la caja del día:', error);
    throw error;
  }
};

/**
 * Abrir la caja del día (solo cajeros, 1 por día por usuario)
 * @param {Object} data - { valor_inicial, descripcion }
 * @returns {Promise<Object>} Caja creada
 */
export const createCaja = async (data) => {
  try {
    const response = await api.post('/cajas', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear caja:', error);
    throw error;
  }
};

/**
 * Actualizar una caja
 * @param {string|number} id - ID de la caja
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Caja actualizada
 */
export const updateCaja = async (id, data) => {
  try {
    const response = await api.put(`/cajas/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar caja:', error);
    throw error;
  }
};

/**
 * Cerrar la caja del día con el Registro de Efectivo:
 * detalle por denominación (billetes y monedas) del dinero físico que se entrega.
 * Tras el cierre la caja ya no admite ediciones ni movimientos.
 * @param {string|number} id - ID de la caja
 * @param {Array} denominaciones - [{ tipo, denominacion, cantidad }]
 * @returns {Promise<Object>} Caja cerrada (incluye diferencia_cierre)
 */
export const cerrarCaja = async (id, denominaciones = []) => {
  try {
    const response = await api.patch(`/cajas/${id}/cerrar`, { denominaciones });
    return response.data;
  } catch (error) {
    console.error('Error al cerrar caja:', error);
    throw error;
  }
};

/**
 * Descargar el Resumen de Caja en PDF (A4)
 * @param {string|number} id - ID de la caja
 * @param {string} nombre - Nombre sugerido del archivo
 */
export const downloadResumenCaja = async (id, nombre = null) => {
  try {
    const response = await api.get(`/cajas/${id}/resumen/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombre || `resumen_caja_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar el resumen de caja:', error);
    throw error;
  }
};

/**
 * Imprimir el Resumen de Caja: abre el PDF en un iframe oculto
 * y lanza el diálogo de impresión del navegador
 * @param {string|number} id - ID de la caja
 */
export const imprimirResumenCaja = async (id) => {
  try {
    const response = await api.get(`/cajas/${id}/resumen/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };
  } catch (error) {
    console.error('Error al imprimir el resumen de caja:', error);
    throw error;
  }
};

// ============================================================================
// CATEGORÍAS
// ============================================================================

/**
 * Obtener categorías de movimientos
 * @param {Object} filters - Filtros (tipo: ingreso|egreso, activa)
 * @returns {Promise<Array>} Lista de categorías
 */
export const getCategorias = async (filters = {}) => {
  try {
    const response = await api.get('/cajas/categorias', { params: filters });
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener categorías:', error);
    throw error;
  }
};

/**
 * Crear categoría
 * @param {Object} data - { nombre, tipo, descripcion }
 * @returns {Promise<Object>} Categoría creada
 */
export const createCategoria = async (data) => {
  try {
    const response = await api.post('/cajas/categorias', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear categoría:', error);
    throw error;
  }
};

/**
 * Actualizar categoría
 * @param {string|number} id - ID de la categoría
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Categoría actualizada
 */
export const updateCategoria = async (id, data) => {
  try {
    const response = await api.put(`/cajas/categorias/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar categoría:', error);
    throw error;
  }
};

// ============================================================================
// MOVIMIENTOS
// ============================================================================

/**
 * Obtener movimientos de caja (cronológico, con filtros y paginación)
 * @param {Object} filters - caja_id, usuario_id, categoria_id, tipo, estado,
 *                           fecha_inicio, fecha_fin, orden, page, limit
 * @returns {Promise<Object>} { movimientos, pagination }
 */
export const getMovimientos = async (filters = {}) => {
  try {
    const response = await api.get('/cajas/movimientos', { params: filters });
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener movimientos:', error);
    throw error;
  }
};

/**
 * Obtener un movimiento por ID (incluye documentos de respaldo)
 * @param {string|number} id - ID del movimiento
 * @returns {Promise<Object>} Movimiento
 */
export const getMovimientoById = async (id) => {
  try {
    const response = await api.get(`/cajas/movimientos/${id}`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener movimiento:', error);
    throw error;
  }
};

/**
 * Registrar un movimiento de caja.
 * Envía multipart/form-data con:
 * - denominaciones: detalle JSON de monedas/billetes [{tipo, denominacion, cantidad}]
 * - billetes: JSON de billetes $50/$100 [{denominacion, numero_serie, factura, cliente, foto_index}]
 * - fotos_billetes: fotografías de los billetes (el orden define foto_index)
 * - documentos: archivos de respaldo generales (máx. 5)
 * @param {Object} data - Campos del movimiento
 * @param {Array} denominaciones - Desglose de efectivo
 * @param {File[]} documentos - Archivos de respaldo
 * @param {Array} billetes - Identificación de billetes de alta denominación
 * @param {File[]} fotosBilletes - Fotos de los billetes (orden = foto_index)
 * @returns {Promise<Object>} Movimiento creado
 */
export const createMovimiento = async (
  data,
  denominaciones = [],
  documentos = [],
  billetes = [],
  fotosBilletes = []
) => {
  try {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value);
      }
    });
    formData.append('denominaciones', JSON.stringify(denominaciones));
    if (billetes.length > 0) {
      formData.append('billetes', JSON.stringify(billetes));
    }
    documentos.forEach((file) => {
      formData.append('documentos', file);
    });
    fotosBilletes.forEach((file) => {
      formData.append('fotos_billetes', file);
    });

    const response = await api.post('/cajas/movimientos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    console.error('Error al registrar movimiento:', error);
    throw error;
  }
};

// ============================================================================
// DENOMINACIONES Y BILLETES
// ============================================================================

/**
 * Catálogo de denominaciones USD (1¢ a $100)
 * @returns {Promise<Array>} [{tipo, denominacion, etiqueta}]
 */
export const getCatalogoDenominaciones = async () => {
  try {
    const response = await api.get('/cajas/denominaciones/catalogo');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener catálogo de denominaciones:', error);
    throw error;
  }
};

/**
 * Desglose actual de monedas/billetes de una caja
 * @param {string|number} cajaId - ID de la caja
 * @returns {Promise<Object>} { saldo_actual, total_desglose, cuadra, denominaciones }
 */
export const getCajaDenominaciones = async (cajaId) => {
  try {
    const response = await api.get(`/cajas/${cajaId}/denominaciones`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener desglose de la caja:', error);
    throw error;
  }
};

/**
 * Obtener la fotografía de un billete como URL de objeto (para vista previa
 * en <img>; la API exige token, por lo que no sirve la URL directa).
 * El llamador debe liberar la URL con window.URL.revokeObjectURL().
 * @param {string|number} billeteId - ID del billete
 * @returns {Promise<string>} URL de objeto de la imagen
 */
export const getBilleteFotoUrl = async (billeteId) => {
  try {
    const response = await api.get(`/cajas/billetes/${billeteId}/foto`, {
      responseType: 'blob',
    });
    return window.URL.createObjectURL(response.data);
  } catch (error) {
    console.error('Error al obtener la foto del billete:', error);
    throw error;
  }
};

/**
 * Descargar la fotografía de un billete de $50/$100
 * @param {string|number} billeteId - ID del billete
 * @param {string} nombre - Nombre para guardar
 */
export const downloadBilleteFoto = async (billeteId, nombre = null) => {
  try {
    const response = await api.get(`/cajas/billetes/${billeteId}/foto`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombre || `billete_${billeteId}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar foto del billete:', error);
    throw error;
  }
};

/**
 * Descargar el comprobante PDF de un movimiento (ingreso/egreso/ajuste)
 * @param {string|number} id - ID del movimiento
 */
export const downloadMovimientoPdf = async (id) => {
  try {
    const response = await api.get(`/cajas/movimientos/${id}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `movimiento_caja_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar el comprobante del movimiento:', error);
    throw error;
  }
};

/**
 * Imprimir el comprobante PDF de un movimiento: abre el PDF en un
 * iframe oculto y lanza el diálogo de impresión del navegador
 * @param {string|number} id - ID del movimiento
 */
export const imprimirMovimientoPdf = async (id) => {
  try {
    const response = await api.get(`/cajas/movimientos/${id}/pdf`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };
  } catch (error) {
    console.error('Error al imprimir el comprobante del movimiento:', error);
    throw error;
  }
};

/**
 * Anular un movimiento (revierte el saldo de la caja)
 * @param {string|number} id - ID del movimiento
 * @param {string} motivo - Motivo de anulación
 * @returns {Promise<Object>} Movimiento anulado
 */
export const anularMovimiento = async (id, motivo) => {
  try {
    const response = await api.patch(`/cajas/movimientos/${id}/anular`, { motivo });
    return response.data;
  } catch (error) {
    console.error('Error al anular movimiento:', error);
    throw error;
  }
};

// ============================================================================
// DOCUMENTOS DE RESPALDO
// ============================================================================

/**
 * Agregar documentos de respaldo a un movimiento existente
 * @param {string|number} movimientoId - ID del movimiento
 * @param {File[]} documentos - Archivos a subir
 * @returns {Promise<Object>} Documentos creados
 */
export const addDocumentos = async (movimientoId, documentos) => {
  try {
    const formData = new FormData();
    documentos.forEach((file) => {
      formData.append('documentos', file);
    });

    const response = await api.post(
      `/cajas/movimientos/${movimientoId}/documentos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('Error al subir documentos:', error);
    throw error;
  }
};

/**
 * Descargar un documento de respaldo
 * @param {string|number} docId - ID del documento
 * @param {string} nombreOriginal - Nombre para guardar el archivo
 */
export const downloadDocumento = async (docId, nombreOriginal = null) => {
  try {
    const response = await api.get(`/cajas/documentos/${docId}/descargar`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = nombreOriginal || `documento_${docId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar documento:', error);
    throw error;
  }
};

/**
 * Eliminar un documento de respaldo
 * @param {string|number} docId - ID del documento
 * @returns {Promise<Object>}
 */
export const deleteDocumento = async (docId) => {
  try {
    const response = await api.delete(`/cajas/documentos/${docId}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar documento:', error);
    throw error;
  }
};

// ============================================================================
// REPORTES
// ============================================================================

/**
 * Reporte de efectivo: saldos por caja y total general
 * @returns {Promise<Object>} { total_efectivo, cajas: [...] }
 */
export const getReporteEfectivo = async () => {
  try {
    const response = await api.get('/cajas/reportes/efectivo');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener reporte de efectivo:', error);
    throw error;
  }
};

/**
 * Reporte detallado de movimientos: cronológico, categorizado,
 * con resúmenes por usuario y categoría
 * @param {Object} filters - fecha_inicio, fecha_fin, caja_id, usuario_id, categoria_id, tipo
 * @returns {Promise<Object>} { resumen, movimientos }
 */
export const getReporteMovimientos = async (filters = {}) => {
  try {
    const response = await api.get('/cajas/reportes/movimientos', { params: filters });
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener reporte de movimientos:', error);
    throw error;
  }
};

/**
 * Descargar reporte de movimientos en Excel
 * @param {Object} filters - Mismos filtros del reporte
 */
export const downloadReporteExcel = async (filters = {}) => {
  try {
    const response = await api.get('/cajas/reportes/movimientos/excel', {
      params: filters,
      responseType: 'blob',
    });
    const fecha = new Date().toISOString().split('T')[0];
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte_caja_${fecha}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar reporte Excel:', error);
    throw error;
  }
};

export default {
  getCajas,
  getCajaById,
  getMiCajaHoy,
  createCaja,
  updateCaja,
  cerrarCaja,
  downloadResumenCaja,
  imprimirResumenCaja,
  getCategorias,
  createCategoria,
  updateCategoria,
  getMovimientos,
  getMovimientoById,
  createMovimiento,
  downloadMovimientoPdf,
  imprimirMovimientoPdf,
  anularMovimiento,
  getCatalogoDenominaciones,
  getCajaDenominaciones,
  getBilleteFotoUrl,
  downloadBilleteFoto,
  addDocumentos,
  downloadDocumento,
  deleteDocumento,
  getReporteEfectivo,
  getReporteMovimientos,
  downloadReporteExcel,
};
